import React, { useRef, useState } from 'react';
import {
  Box, Card, CardBody, VStack, Heading, Text, Input, Button,
  FormControl, FormLabel, InputGroup, InputRightElement, IconButton,
  Alert, AlertIcon, useToast,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useTranslation, Trans } from 'react-i18next';
import apiClient from '../services/apiClient';
import TurnstileWidget, { getCaptchaErrorDetail } from './TurnstileWidget';

const Login = ({ onLoginSuccess }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Cloudflare Turnstile token. The backend refuses logins without a valid
  // token, so Sign In stays disabled until the widget yields one.
  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileRef = useRef(null);
  // Emailed one-time code stage (all admin-panel sign-ins, once per 24h).
  const [otpStage, setOtpStage] = useState(null); // {challenge, emailHint}
  const [otpCode, setOtpCode] = useState('');
  const toast = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await apiClient.login(
        username, password, captchaToken,
        otpStage ? { code: otpCode.trim(), challenge: otpStage.challenge } : undefined
      );
      if (data && data.otpRequired) {
        // Correct password; the code is on its way. That request consumed
        // the captcha token, so arm a fresh one for the code submit.
        setOtpStage({ challenge: data.challenge, emailHint: data.emailHint });
        setOtpCode('');
        setCaptchaToken('');
        if (turnstileRef.current) turnstileRef.current.reset();
        setLoading(false);
        return;
      }
      const role = data.user?.user_profile?.role;
      // A Django superuser may have no UserProfile (or a non-admin role) —
      // they still need in, or the superuser-only Channel Manager screen is
      // unreachable for exactly the accounts allowed to use it.
      if (role !== 'admin' && !data.user?.is_superuser) {
        apiClient.logout();
        setCaptchaToken('');
        if (turnstileRef.current) turnstileRef.current.reset();
        setError(t('login.errors.accessDenied'));
        setLoading(false);
        return;
      }
      toast({ title: t('login.toasts.success'), status: 'success', duration: 2000 });
      onLoginSuccess(data.user);
    } catch (err) {
      // Turnstile tokens are single-use — get a fresh one for the retry.
      setCaptchaToken('');
      if (turnstileRef.current) turnstileRef.current.reset();
      // 403 captcha_required/captcha_failed and 429 login_locked carry a
      // user-facing detail; everything else keeps the existing message.
      const code = err.response?.data?.code;
      if (code === 'otp_expired') { setOtpStage(null); setOtpCode(''); }
      setError(getCaptchaErrorDetail(err) || err.response?.data?.detail || t('login.errors.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
      <Card w="400px" shadow="lg">
        <CardBody p={8}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={5}>
              <VStack spacing={1}>
                <Heading size="lg" color="brand.600">{t('common.adminPanel')}</Heading>
                <Text color="gray.500" fontSize="sm">{t('login.subtitle')}</Text>
              </VStack>

              {error && (
                <Alert status="error" borderRadius="md" fontSize="sm">
                  <AlertIcon /> {error}
                </Alert>
              )}

              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('login.labels.username')}</FormLabel>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('login.placeholders.username')}
                  autoFocus
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('login.labels.password')}</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('login.placeholders.password')}
                  />
                  <InputRightElement>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={t('login.labels.togglePassword')}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              {otpStage && (
                <FormControl isRequired>
                  <FormLabel>{t('login.labels.signInCode')}</FormLabel>
                  <Input
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    placeholder="123456"
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="one-time-code"
                    autoFocus
                    fontSize="xl"
                    letterSpacing="6px"
                    textAlign="center"
                  />
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    <Trans
                      i18nKey="login.otp.codeSent"
                      values={{ email: otpStage.emailHint || t('login.otp.yourEmail') }}
                      components={{ b: <b /> }}
                    />{' '}
                    <Text as="span" color="blue.500" cursor="pointer"
                      onClick={() => { setOtpStage(null); setOtpCode(''); setCaptchaToken(''); if (turnstileRef.current) turnstileRef.current.reset(); }}>
                      {t('login.otp.startOver')}
                    </Text>
                  </Text>
                </FormControl>
              )}

              <TurnstileWidget ref={turnstileRef} onToken={setCaptchaToken} />

              <Button
                type="submit"
                colorScheme="blue"
                width="100%"
                isLoading={loading}
                isDisabled={!captchaToken || (otpStage && otpCode.trim().length < 6)}
                loadingText={t('login.buttons.signingIn')}
              >
                {otpStage ? t('login.buttons.verifyCode') : t('login.buttons.signIn')}
              </Button>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
};

export default Login;
